import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ApiError, api, type SearchResponse, type SearchResult, type SourceInfo, type SourceRole } from '../api';
import { useSurfaceOpen, TitleButton } from '../components/cards';
import { useOpenDetail } from '../components/detail-link';
import { FollowButton, SubjectChips } from '../components/follow';
import { ErrorState, Loading, PageIntro, useAsync } from '../components/ui';
import { RecordNotes, ROLE_LABELS } from '../components/sources';
import { formatDate, formatDateTime, formatTime, localToday, plural, relativeAge } from '../format';
import {
  AGE_GROUP_LABELS,
  PAGE_SIZE,
  detectPreset,
  presetRange,
  readSearchState,
  toApiParams,
  withChange,
  writeSearchState,
  type PeriodPreset,
  type SearchUrlState,
} from './searchState';

const DEBOUNCE_MS = 350;

const SORT_LABELS: Record<string, string> = {
  relevance: 'Relevanz',
  newest: 'Neueste zuerst',
  oldest: 'Älteste zuerst',
  updated: 'Zuletzt aktualisiert',
  source: 'Quelle A–Z',
};

export function SearchPage() {
  const [params, setParams] = useSearchParams();
  const state = useMemo(() => readSearchState(params), [params]);
  const apiKey = useMemo(() => JSON.stringify(toApiParams(state)), [state]);

  const update = (change: Partial<SearchUrlState>, opts: { replace?: boolean } = {}) => {
    const next = writeSearchState(withChange(state, change));
    // Keep an open details parameter where it is.
    const karte = params.get('karte');
    if (karte) next.set('karte', karte);
    setParams(next, { replace: opts.replace });
  };

  // Typing is debounced into the URL; history is replaced while typing so Back
  // does not step through every keystroke. Submitting pushes.
  const [draft, setDraft] = useState(state.q);
  const lastSent = useRef(state.q);
  useEffect(() => {
    if (state.q !== lastSent.current) {
      lastSent.current = state.q;
      setDraft(state.q);
    }
  }, [state.q]);
  useEffect(() => {
    if (draft.trim() === lastSent.current) return;
    const t = window.setTimeout(() => {
      lastSent.current = draft.trim();
      update({ q: draft.trim() }, { replace: true });
    }, DEBOUNCE_MS);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft]);

  const result = useSearch(apiKey);
  const catalogue = useAsync(() => api.sources(), []);

  const reset = () => {
    lastSent.current = '';
    setDraft('');
    setParams(new URLSearchParams());
  };

  return (
    <>
      <PageIntro eyebrow="Archiv & Recherche" title="Was suchst du?">
        Quellen, Zeitraum und Reihenfolge direkt einstellen. Die Suche läuft über alle gespeicherten Meldungen.
      </PageIntro>

      <form
        className="search-box"
        role="search"
        onSubmit={(e) => {
          e.preventDefault();
          lastSent.current = draft.trim();
          update({ q: draft.trim() });
        }}
      >
        <label htmlFor="search-q" className="visually-hidden">
          Suchbegriff
        </label>
        <input
          id="search-q"
          type="search"
          value={draft}
          placeholder="Thema, Ort oder Begriff …"
          autoComplete="off"
          onChange={(e) => setDraft(e.target.value)}
        />
        <button className="button primary" type="submit">
          Suchen
        </button>
      </form>

      <Filters
        state={state}
        update={update}
        reset={reset}
        facets={result.data?.facets ?? null}
        catalogue={catalogue.data?.sources ?? null}
      />

      <ActiveFilters state={state} update={update} reset={reset} data={result.data} catalogue={catalogue.data?.sources ?? null} />

      <section aria-labelledby="results-h" className="results">
        <div className="results-head">
          <h2 id="results-h" className="visually-hidden">
            Suchergebnisse
          </h2>
          <p role="status" aria-live="polite" className="result-count">
            {result.loading
              ? 'Suche läuft…'
              : result.data
                ? `${plural(result.data.total, 'Treffer', 'Treffer')}${result.data.total > 0 ? ` · ${pageLabel(result.data)}` : ''}`
                : ''}
          </p>
          <div className="sorters">
            <label>
              Sortieren nach
              <select value={state.sort || (state.q ? 'relevance' : 'newest')} onChange={(e) => update({ sort: e.target.value as SearchUrlState['sort'] })}>
                {Object.entries(SORT_LABELS).map(([k, l]) => (
                  <option key={k} value={k} disabled={k === 'relevance' && !state.q}>
                    {l}
                    {k === 'relevance' && !state.q ? ' (braucht Suchbegriff)' : ''}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Gruppieren nach
              <select value={state.group} onChange={(e) => update({ group: e.target.value as SearchUrlState['group'] })}>
                <option value="none">Keine Gruppierung</option>
                <option value="topic">Thema</option>
                <option value="source">Quelle</option>
                <option value="age">Alter</option>
              </select>
            </label>
          </div>
        </div>
        {state.group === 'source' && (
          <p className="help small">
            Gruppiert nach Quelle erscheint jede Meldung nur einmal: unter der alphabetisch ersten passenden Quelle (bei gewählten Quellen
            unter der ersten gewählten). Alle beteiligten Quellen bleiben an der Meldung sichtbar.
          </p>
        )}

        {result.error && <SearchError error={result.error} reset={reset} retry={result.retry} />}
        {result.loading && !result.data && <Loading label="Suche läuft…" />}
        {result.data && result.data.results.length === 0 && !result.error && (
          <div className="state state-empty">
            <h2>Keine Treffer.</h2>
            <p className="muted">
              {state.q ? `Nichts passt zu „${state.q}“` : 'Nichts passt'}
              {hasFilters(state) ? ' mit diesen Filtern. Entferne einen Filter oder wähle einen grösseren Zeitraum.' : '. Versuche einen anderen Begriff.'}
            </p>
            {(hasFilters(state) || state.q) && (
              <button type="button" className="small-button" onClick={reset}>
                Suche zurücksetzen
              </button>
            )}
          </div>
        )}
        {result.data && result.data.results.length > 0 && (
          <div className={result.loading ? 'is-refreshing' : undefined} aria-busy={result.loading}>
            <ResultList data={result.data} grouped={state.group !== 'none'} />
            <Pager data={result.data} state={state} update={update} />
          </div>
        )}
      </section>
    </>
  );
}

function pageLabel(d: SearchResponse) {
  const page = Math.floor(d.offset / d.limit) + 1;
  const pages = Math.max(1, Math.ceil(d.total / d.limit));
  return `Seite ${page} von ${pages} · Stand ${formatTime(d.as_of)}`;
}

function hasFilters(s: SearchUrlState) {
  return s.source.length > 0 || s.topic.length > 0 || !!s.section || !!s.from || !!s.to;
}

/** Fetches with cancellation, so a slow earlier response never overwrites a newer one. */
function useSearch(key: string) {
  const [data, setData] = useState<SearchResponse | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [loading, setLoading] = useState(true);
  const [nonce, setNonce] = useState(0);
  useEffect(() => {
    const ctrl = new AbortController();
    setLoading(true);
    api
      .search(JSON.parse(key), { signal: ctrl.signal })
      .then((d) => {
        setData(d);
        setError(null);
      })
      .catch((e) => {
        if (ctrl.signal.aborted) return;
        setError(e instanceof Error ? e : new Error(String(e)));
        setData(null);
      })
      .finally(() => {
        if (!ctrl.signal.aborted) setLoading(false);
      });
    return () => ctrl.abort();
  }, [key, nonce]);
  return { data, error, loading, retry: () => setNonce((n) => n + 1) };
}

function SearchError({ error, reset, retry }: { error: Error; reset: () => void; retry: () => void }) {
  if (error instanceof ApiError && error.status === 400) {
    return (
      <div className="state state-error" role="alert">
        <h2>Diese Suche ist ungültig.</h2>
        <p>{error.message}</p>
        <button type="button" className="small-button" onClick={reset}>
          Suche zurücksetzen
        </button>
      </div>
    );
  }
  return <ErrorState error={error} onRetry={retry} />;
}

function Filters({
  state,
  update,
  reset,
  facets,
  catalogue,
}: {
  state: SearchUrlState;
  update: (c: Partial<SearchUrlState>) => void;
  reset: () => void;
  facets: SearchResponse['facets'] | null;
  catalogue: SourceInfo[] | null;
}) {
  const today = localToday();
  const preset = detectPreset(state, today);
  const [customOpen, setCustomOpen] = useState(preset === 'custom');
  const shownPreset: PeriodPreset = customOpen ? 'custom' : preset;

  return (
    <div className="filters panel" role="group" aria-label="Suchfilter">
      <SourceFilter state={state} update={update} facets={facets} catalogue={catalogue} />

      <div className="filter-row">
        <label className="field-label" htmlFor="f-period">
          Zeitraum
        </label>
        <select
          id="f-period"
          value={shownPreset}
          onChange={(e) => {
            const p = e.target.value as PeriodPreset;
            setCustomOpen(p === 'custom');
            if (p !== 'custom') update(presetRange(p, today));
          }}
        >
          <option value="any">Jeder Zeitraum</option>
          <option value="since-yesterday">Seit gestern</option>
          <option value="7d">Letzte 7 Tage</option>
          <option value="30d">Letzte 30 Tage</option>
          <option value="custom">Eigener Zeitraum …</option>
        </select>

        <label className="field-label" htmlFor="f-datefield">
          Datum
        </label>
        <select id="f-datefield" value={state.date_field} onChange={(e) => update({ date_field: e.target.value as SearchUrlState['date_field'] })}>
          <option value="published">Veröffentlichung</option>
          <option value="updated">Letzte Aktualisierung</option>
          <option value="event">Veranstaltungsdatum</option>
        </select>

        <label className="field-label" htmlFor="f-section">
          Bereich
        </label>
        <select id="f-section" value={state.section} onChange={(e) => update({ section: e.target.value })}>
          <option value="">Alle</option>
          <option value="news">Nachrichten</option>
          <option value="crossfit">CrossFit</option>
          <option value="events">Veranstaltungen</option>
        </select>

        <button type="button" className="text-button reset" onClick={reset}>
          Filter zurücksetzen
        </button>
      </div>

      {shownPreset === 'custom' && (
        <div className="filter-row">
          <label className="field-label" htmlFor="f-from">
            Von
          </label>
          <input id="f-from" type="date" value={state.from} max={state.to || undefined} onChange={(e) => update({ from: e.target.value })} />
          <label className="field-label" htmlFor="f-to">
            Bis
          </label>
          <input id="f-to" type="date" value={state.to} min={state.from || undefined} onChange={(e) => update({ to: e.target.value })} />
          <span className="muted small">Kalendertage in Europe/Zurich, Enddatum eingeschlossen.</span>
        </div>
      )}

      {facets && facets.topics.length > 0 && (
        <div className="filter-row" role="group" aria-labelledby="f-topic-label">
          <span className="field-label" id="f-topic-label">
            Thema
          </span>
          <div className="chip-toggles">
            {facets.topics.filter((t) => t.count > 0 || state.topic.includes(t.id)).map((t) => {
              const on = state.topic.includes(t.id);
              return (
                <button
                  key={t.id}
                  type="button"
                  aria-pressed={on}
                  onClick={() => update({ topic: on ? state.topic.filter((x) => x !== t.id) : [...state.topic, t.id] })}
                >
                  {t.label} <span className="count">{t.count}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Sources: OR within the selection, AND with the other filters. The catalogue
 * will grow, so the list is filterable and collapsible; counts come from the
 * current result set.
 */
function SourceFilter({
  state,
  update,
  facets,
  catalogue,
}: {
  state: SearchUrlState;
  update: (c: Partial<SearchUrlState>) => void;
  facets: SearchResponse['facets'] | null;
  catalogue: SourceInfo[] | null;
}) {
  const [filter, setFilter] = useState('');
  const counts = new Map((facets?.sources ?? []).map((s) => [s.id, s.count]));
  const byId = new Map<string, { id: string; name: string; role: SourceRole }>();
  // The list offers the sources of the chosen area; disabled sources only if they have results.
  for (const s of catalogue ?? []) {
    const areaMatch = state.section ? s.section === state.section : s.section !== 'crossfit';
    if (!areaMatch) continue;
    if (!s.enabled && !(facets?.sources ?? []).some((f) => f.id === s.id && f.count > 0)) continue;
    byId.set(s.id, { id: s.id, name: s.name, role: s.role ?? 'journalism' });
  }
  for (const s of facets?.sources ?? []) if (!byId.has(s.id) && s.count > 0) byId.set(s.id, { id: s.id, name: s.name, role: s.role ?? 'journalism' });
  for (const id of state.source) if (!byId.has(id)) byId.set(id, { id, name: id, role: 'journalism' });
  const roleOrder: SourceRole[] = ['journalism', 'official', 'parliament', 'index', 'events'];
  const all = [...byId.values()].sort(
    (a, b) => roleOrder.indexOf(a.role) - roleOrder.indexOf(b.role) || a.name.localeCompare(b.name, 'de-CH'),
  );
  const grouped = new Set(all.map((s) => s.role)).size > 1;
  const needle = filter.trim().toLocaleLowerCase('de-CH');
  const list = needle ? all.filter((s) => s.name.toLocaleLowerCase('de-CH').includes(needle) || s.id.includes(needle)) : all;

  return (
    <details className="source-filter" open={all.length <= 12 || undefined}>
      <summary>
        Quellen{state.source.length > 0 ? ` · ${state.source.length} ausgewählt` : ' · alle'}
      </summary>
      {all.length > 8 && (
        <>
          <label htmlFor="f-source-filter" className="visually-hidden">
            Quellen durchsuchen
          </label>
          <input
            id="f-source-filter"
            type="search"
            className="source-search"
            placeholder="Quelle suchen …"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
        </>
      )}
      <fieldset>
        <legend className="visually-hidden">Quellen auswählen</legend>
        <div className="source-checks">
          {list.map((s, i) => {
            const on = state.source.includes(s.id);
            const n = counts.get(s.id);
            const heading = grouped && (i === 0 || list[i - 1].role !== s.role);
            return (
              <Fragment key={s.id}>
              {heading && <span className="role-group">{ROLE_LABELS[s.role]}</span>}
              <label className="check">
                <input
                  type="checkbox"
                  checked={on}
                  onChange={() => update({ source: on ? state.source.filter((x) => x !== s.id) : [...state.source, s.id] })}
                />
                {s.name}
                {facets && <span className="count">{n ?? 0}</span>}
              </label>
              </Fragment>
            );
          })}
          {list.length === 0 && <span className="muted small">Keine Quelle passt zu „{filter}“.</span>}
        </div>
      </fieldset>
      <p className="muted small">Mehrere Quellen: eine Meldung passt, wenn eine ihrer Quellen gewählt ist.</p>
    </details>
  );
}

function ActiveFilters({
  state,
  update,
  reset,
  data,
  catalogue,
}: {
  state: SearchUrlState;
  update: (c: Partial<SearchUrlState>) => void;
  reset: () => void;
  data: SearchResponse | null;
  catalogue: SourceInfo[] | null;
}) {
  const sourceName = (id: string) =>
    data?.facets.sources.find((s) => s.id === id)?.name ?? catalogue?.find((s) => s.id === id)?.name ?? id;
  const topicName = (id: string) => data?.facets.topics.find((t) => t.id === id)?.label ?? id;
  const dateWord = state.date_field === 'updated' ? 'Aktualisiert' : state.date_field === 'event' ? 'Termin' : 'Veröffentlicht';
  const chips: Array<{ key: string; label: string; remove: () => void }> = [];
  if (state.q) chips.push({ key: 'q', label: `Begriff: ${state.q}`, remove: () => update({ q: '' }) });
  for (const id of state.source)
    chips.push({ key: `s-${id}`, label: `Quelle: ${sourceName(id)}`, remove: () => update({ source: state.source.filter((x) => x !== id) }) });
  for (const id of state.topic)
    chips.push({ key: `t-${id}`, label: `Thema: ${topicName(id)}`, remove: () => update({ topic: state.topic.filter((x) => x !== id) }) });
  if (state.section)
    chips.push({ key: 'sec', label: `Bereich: ${({ news: 'Nachrichten', crossfit: 'CrossFit', events: 'Veranstaltungen' } as Record<string, string>)[state.section] ?? state.section}`, remove: () => update({ section: '' }) });
  if (state.from) chips.push({ key: 'from', label: `${dateWord} ab ${state.from}`, remove: () => update({ from: '' }) });
  if (state.to) chips.push({ key: 'to', label: `${dateWord} bis ${state.to}`, remove: () => update({ to: '' }) });
  if (chips.length === 0) return null;
  return (
    <div className="active-filters" aria-label="Aktive Filter" role="group">
      {chips.map((c) => (
        <button key={c.key} type="button" className="filter-chip" onClick={c.remove} aria-label={`Filter entfernen: ${c.label}`}>
          {c.label} <span aria-hidden="true">×</span>
        </button>
      ))}
      <button type="button" className="text-button" onClick={reset}>
        Alle zurücksetzen
      </button>
    </div>
  );
}

function groupHeading(r: SearchResult, group: string): string {
  if (group === 'age' && r.search.group_key) return AGE_GROUP_LABELS[r.search.group_key] ?? r.search.group_label ?? r.search.group_key;
  if (r.search.group_label) return r.search.group_label;
  if (group === 'topic') return 'Ohne Thema';
  return 'Ohne Zuordnung';
}

function ResultList({ data, grouped }: { data: SearchResponse; grouped: boolean }) {
  if (!grouped) {
    return (
      <div className="result-list">
        {data.results.map((r) => (
          <ResultRow key={r.version.id} r={r} />
        ))}
      </div>
    );
  }
  // The server orders by group, then by the chosen sort; headers follow its order.
  const blocks: Array<{ key: string; label: string; items: SearchResult[] }> = [];
  for (const r of data.results) {
    const key = r.search.group_key ?? '__none';
    const last = blocks[blocks.length - 1];
    if (last && last.key === key) last.items.push(r);
    else blocks.push({ key, label: groupHeading(r, data.group), items: [r] });
  }
  return (
    <>
      {blocks.map((b, i) => (
        <section key={`${b.key}-${i}`} aria-label={b.label}>
          <h3 className="result-group">
            {b.label} · {b.items.length}
            {i === 0 && data.offset > 0 ? ' (Fortsetzung)' : ''}
          </h3>
          <div className="result-list">
            {b.items.map((r) => (
              <ResultRow key={r.version.id} r={r} />
            ))}
          </div>
        </section>
      ))}
    </>
  );
}

/** A date-only event has no published start; showing local midnight would invent a time. */
function eventDateOnly(r: SearchResult): boolean {
  return (r.version.payload as { date_precision?: string } | null)?.date_precision === 'date';
}

function ResultRow({ r }: { r: SearchResult }) {
  const onSurface = useSurfaceOpen(r.version.id);
  const open = useOpenDetail();
  const s = r.search;
  const names = s.sources.length > 0 ? s.sources.map((x) => x.name) : [];
  const updatedDiffers = !s.published_at || Math.abs(Date.parse(s.updated_at) - Date.parse(s.published_at)) > 30 * 60_000;
  return (
    <article className="result" onClick={onSurface} data-version={r.version.id}>
      <div className="meta">
        {r.topic && <span className="tag">{r.topic.label}</span>}
        {names.length > 0 && <span>{names.join(' · ')}</span>}
        {s.event_at && (
          <span>
            Termin{' '}
            <time dateTime={s.event_at}>{eventDateOnly(r) ? formatDate(s.event_at) : formatDateTime(s.event_at)}</time>
          </span>
        )}
        <span>
          {s.published_at ? (
            <time dateTime={s.published_at} title={formatDateTime(s.published_at)}>
              Veröffentlicht {relativeAge(s.published_at)}
            </time>
          ) : (
            'Datum unbekannt'
          )}
        </span>
      </div>
      <TitleButton view={r} as="h3" />
      <SubjectChips view={r} />
      <p className="summary">{r.version.summary}</p>
      <RecordNotes view={r} />
      <div className="meta">
        {updatedDiffers && (
          <span>
            Letzte Version <time dateTime={s.updated_at}>{relativeAge(s.updated_at)}</time>
          </span>
        )}
        <span>{s.version_count > 1 ? `${s.version_count} Versionen` : 'Eine Version'}</span>
        <span>{names.length > 1 ? `${names.length} Quellen, eine Meldung` : 'Ein Bericht'}</span>
        {r.version.degraded && <span className="tag tag-warn">ohne Modell</span>}
      </div>
      <div className="actions">
        <button
          type="button"
          className="small-button"
          aria-label={`Lesen und Quellen: ${r.version.title}`}
          onClick={(e) => open(r.version.id, e.currentTarget)}
        >
          Lesen &amp; Quellen{names.length ? ` (${names.length})` : ''}
        </button>
        <FollowButton view={r} />
      </div>
    </article>
  );
}

function Pager({ data, state, update }: { data: SearchResponse; state: SearchUrlState; update: (c: Partial<SearchUrlState>) => void }) {
  if (data.total <= data.limit && data.offset === 0) return null;
  const prev = data.offset > 0 ? Math.max(0, data.offset - PAGE_SIZE) : null;
  const asOf = state.as_of || data.as_of;
  return (
    <nav className="pager" aria-label="Seiten">
      <button
        type="button"
        className="small-button"
        disabled={prev === null}
        onClick={() => {
          update({ offset: prev ?? 0, as_of: prev ? asOf : '' });
          document.getElementById('results-h')?.scrollIntoView?.({ block: 'start' });
        }}
      >
        ← Vorherige
      </button>
      <span className="muted small">
        {data.offset + 1}–{data.offset + data.results.length} von {data.total}
      </span>
      <button
        type="button"
        className="small-button"
        disabled={data.next_offset === null}
        onClick={() => {
          // The first page's as_of pins the result set: no repeats or skips while paging.
          update({ offset: data.next_offset ?? 0, as_of: asOf });
          document.getElementById('results-h')?.scrollIntoView?.({ block: 'start' });
        }}
      >
        Nächste →
      </button>
    </nav>
  );
}
